import type { BannerLangViewModel, BannerViewModel } from '@/shared/api/generated';
import {
  BANNER_LANGUAGES,
  BANNER_MEDIA_KIND,
  type BannerFormValues,
  type BannerTranslationFormValue,
  EMPTY_BANNER_TRANSLATION,
  INITIAL_BANNER_FORM_VALUES,
} from '../types/bannerForm';
import { isVideoUrl } from './bannerMedia';

/**
 * Corta um `date-time` da API para o `YYYY-MM-DD` que o `DatePicker` usa.
 *
 * Corta a string em vez de converter para `Date`: `2025-06-30T00:00:00Z` viraria
 * `29/06` no fuso local (UTC-3), fazendo o banner "começar um dia antes".
 */
function toDateInput(value: string | null | undefined): string {
  return value ? value.slice(0, 10) : '';
}

/** Monta o valor de uma aba de tradução a partir do registro de idioma da API. */
function toTranslation(lang: BannerLangViewModel | undefined): BannerTranslationFormValue {
  if (!lang) return EMPTY_BANNER_TRANSLATION;

  const mediaUrl = lang.filePathMobile;
  const hasVideo = isVideoUrl(mediaUrl);

  return {
    url: lang.url ?? '',
    imageFile: null,
    imageDesktopFile: null,
    videoFile: null,
    ...(lang.id !== undefined ? { langId: lang.id } : {}),
    ...(mediaUrl && !hasVideo ? { existingImageUrl: mediaUrl } : {}),
    ...(lang.filePath && !isVideoUrl(lang.filePath)
      ? { existingImageDesktopUrl: lang.filePath }
      : {}),
    ...(mediaUrl && hasVideo ? { existingVideoUrl: mediaUrl } : {}),
  };
}

/**
 * Converte o banner (e seus idiomas) vindos da API nos valores do formulário.
 *
 * O modo da mídia sai da extensão de `filePathMobile` (o par mobile é o que a tela
 * usa): o cadastro guarda imagem e vídeo no mesmo campo, então um `.mp4` abre a
 * tela em modo vídeo — antes abria como imagem e o arquivo quebrava num `<img>`.
 *
 * @param banner - Banner retornado por `GET /api/v1/banner/{id}`.
 * @param langs - Idiomas retornados por `GET /api/v1/banner/{id}/langs`.
 * @returns Valores prontos para o `reset` do React Hook Form.
 *
 * @example
 * ```ts
 * reset(mapBannerToForm(banner, langs));
 * ```
 */
export function mapBannerToForm(
  banner: BannerViewModel,
  langs: readonly BannerLangViewModel[] = [],
): BannerFormValues {
  // A mídia do banner é a do par mobile — é ela que a tela mostra e envia.
  const mediaUrl = banner.filePathMobile;
  const hasVideo = isVideoUrl(mediaUrl);

  const byCulture = (culture: string) =>
    langs.find(lang => lang.cultureInfo?.toLowerCase() === culture.toLowerCase());

  return {
    ...INITIAL_BANNER_FORM_VALUES,
    ...(banner.companyId != null ? { companyId: banner.companyId } : { companyId: undefined }),
    title: banner.title ?? '',
    startDate: toDateInput(banner.startDate),
    endDate: toDateInput(banner.endDate),
    priority: banner.priority ?? INITIAL_BANNER_FORM_VALUES.priority,
    // Sem campo em tela, mas preservada: sobrescrever com o default apagaria o
    // que já estava configurado no banner.
    position: banner.position ?? INITIAL_BANNER_FORM_VALUES.position,
    url: banner.url ?? '',
    duration: banner.duration && banner.duration > 0 ? banner.duration : 1,
    showTitle: banner.showTitle ?? false,
    general: banner.general ?? false,
    active: banner.active ?? false,
    isMonograph: banner.isMonograph ?? false,
    isMonitor: banner.isMonitor ?? false,
    classStudents: (banner.classStudents ?? []).map(cs => ({
      classStudentId: cs.classStudentId ?? 0,
      description: cs.description ?? '',
    })),
    // O cadastro devolve um caminho só (`filePathMobile`) para imagem e vídeo: a
    // extensão é o que diz em qual modo a tela precisa abrir.
    mediaKind: hasVideo ? BANNER_MEDIA_KIND.Video : BANNER_MEDIA_KIND.Image,
    imageFile: null,
    ...(mediaUrl && !hasVideo ? { existingImageUrl: mediaUrl } : {}),
    imageDesktopFile: null,
    // O par legado vem de `filePath`; só faz sentido como imagem.
    ...(banner.filePath && !isVideoUrl(banner.filePath)
      ? { existingImageDesktopUrl: banner.filePath }
      : {}),
    videoFile: null,
    ...(hasVideo && mediaUrl ? { existingVideoUrl: mediaUrl } : {}),
    videoDurationSeconds: null,
    // Qual tipo está no cadastro: trocar de tipo passa a exigir um arquivo novo,
    // porque o cadastro substitui a mídia em vez de apagá-la.
    savedMediaKind: mediaUrl
      ? hasVideo
        ? BANNER_MEDIA_KIND.Video
        : BANNER_MEDIA_KIND.Image
      : null,
    translations: {
      [BANNER_LANGUAGES.EnUs]: toTranslation(byCulture(BANNER_LANGUAGES.EnUs)),
      [BANNER_LANGUAGES.EsEs]: toTranslation(byCulture(BANNER_LANGUAGES.EsEs)),
    },
  };
}
